import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {
    getIntegrationLogs,
    clearIntegrationLogs,
    initPageAndSortThenGetIntegrationLogsList
} from "../../actions/integrationLogsActions";
import { updatePage, updateSort } from "../../actions/pageAndSortActions";
import { openDrawer } from "../../actions/drawerActions";
import {getTemplateList} from "../../actions/templateListActions";
import TableViewHook from "../../containers/TableViewHook";
import TableGridHook from "../../containers/TableGridHook";
import IntegrationLogDetailsDrawerHook, { integrationLogDetailsDrawerProps } from "../drawers/IntegrationLogDetailsDrawerHook";
import ArgoListTitleAndLoadingAnimationHook from "../common/ArgoListTitleAndLoadingAnimationHook";
import FilterPopOverHook from "../dialogs/filters/FilterPopOverHook";
import * as TEMPLATE from "../../constants/templateConstants";
import * as SYSTEM from "../../constants/systemConstants";


function IntegrationLogsListHook(props) {
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(false);
    const [id, setId] = useState(null);
    const [displayIntegrationName, setDisplayIntegrationName] = useState("");

    const dispatch = useDispatch();
    const integrationLogs = useSelector(state => state.integrationLogsList.list);
    const integrationId = useSelector(state => parseInt(state.integrationLogsList.id, 10));
    const integrationName = useSelector(state => state.integrationName.name);
    const filterState = useSelector(state => state.filter[TEMPLATE.FILTER.all.integrationLogs] || {});

    useEffect(() => {
        const pathArray = window.location.pathname.split('/');
        const pathId = parseInt(pathArray[pathArray.length - 1]);
        setLoading(true);
        setId(pathId);
        dispatch(initPageAndSortThenGetIntegrationLogsList(
            pathId, 
            integrationName, 
            {
                pageNumber: filterState.pageNumber, 
                pageSize: filterState.pageSize, 
                name: filterState.name, 
                direction: filterState.direction
            }
        ));

        return () => {
            dispatch(clearIntegrationLogs());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {

        if (integrationLogs.length && !integrationName) {
            setDisplayIntegrationName(integrationLogs[0].integrationEvent.integration.name);
        } else {
            setDisplayIntegrationName(integrationName);
        }

    }, [integrationLogs, integrationName]);

    useEffect(() => {
        // Called twice, first pass set filter false, then on second call list is loaded and loading animation can be stopped
        if (filter) {
            setFilter(false);
        }
        else if (loading) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            }
            else {
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, integrationLogs]);

    // Used to test loading animation rendering
    const delayedSetLoadingFalse = () => {
        setLoading(false);
    };

    const setLoadingStateTrue = (callback, parm1 = null) => {
        // If a parameter is passed then pass it to call back, should be the template of the item to be saved edit or created
        if (parm1 === null) {
            setLoading(true);
            setFilter(false);
            callback();
        } else {
            setLoading(true);
            setFilter(false);
            callback(parm1);
        }
    };


    const onPageChange = (pageNumber, pageSize, event) => {
        dispatch(updatePage(TEMPLATE.FILTER.all.integrationLogs, pageNumber, pageSize, event));
        setLoading(true);
        setFilter(false);
        dispatch(getIntegrationLogs(
            integrationId,
            integrationName,
            {
                pageNumber: pageNumber,
                pageSize: pageSize,
                name: filterState.name,
                direction: filterState.direction
            },
            filterState.ucid,
            filterState.questionnaire
        ));
    };

    const onSortChange = (name, direction) => {
        dispatch(updateSort(TEMPLATE.FILTER.all.integrationLogs, name, direction));
        setLoading(true);
        setFilter(false);
        dispatch(getIntegrationLogs(
            integrationId, 
            integrationName, 
            {
                pageNumber: filterState.pageNumber, 
                pageSize: filterState.pageSize, 
                name: name, 
                direction: direction
            },
            filterState.ucid,
            filterState.questionnaire
        ));
    };

    const viewLogDetails = (name, id) => {
        dispatch(openDrawer(integrationLogDetailsDrawerProps, <IntegrationLogDetailsDrawerHook id={id} />));
    };

    // Add custom actions to row actions
    const customActions = [
        {
            func: viewLogDetails,
            label: "Details"
        }
    ];

    return (
        <div id="IntegrationLogsList-container-div" style={{ height: "100%", padding: "15px", marginTop: '64px' }}>

            <ArgoListTitleAndLoadingAnimationHook
                fileName="IntegrationLogsList"
                listTitle={"Integration Logs: " + displayIntegrationName}
                loading={loading}
                filter={filter}
                showOutstandingAlert={false}
            />

            <div style={{ clear: "both" }} />

            <Card id="IntegrationLogsList-card" variant="outlined" sx={{ border: "1px solid lightgray", height: "100%", boxShadow: "2px 4px whitesmoke", overflow: "auto" }}>

                <CardContent id="IntegrationLogsList-card-content">

                    <TableViewHook
                        templateType={TEMPLATE.TYPE.INTEGRATION_LOGS}
                        templateMode={TEMPLATE.MODE.ALL}
                        setLoadingState={setLoadingStateTrue}
                        integrationID={id}
                        filter={
                            <FilterPopOverHook
                                page="integrationsLogList"
                                templateType={TEMPLATE.TYPE.INTEGRATION_LOGS}
                                templateMode={TEMPLATE.MODE.ALL}
                                filterName={TEMPLATE.FILTER.all.integrationLogs}
                                submitValue={getTemplateList}
                                showName={false}
                                showQualifier={false}
                                showUpdatedBy={false}
                                showQuestionnaire={true}
                                showUCID={true}
                                integrationID={id}
                                setLoadingState={setLoadingStateTrue} parentName={"IntegrationLogsList"}
                            />}
                    >
                        <TableGridHook
                            id="IntegrationsLogsList-table-grid"
                            displayType={"integrationLogs"}
                            dataSource={integrationLogs}
                            nameDrawer={viewLogDetails}
                            customActions={customActions}
                            pageState={filterState}
                            onPageChange={onPageChange}
                            sortState={filterState}
                            onSortChange={onSortChange}
                            templateType={TEMPLATE.TYPE.INTEGRATION_LOGS}
                            templateMode={TEMPLATE.MODE.ALL}
                            showQualifier={false}
                            showName={false}
                            showLastUpdated={false}
                            showUpdatedBy={false}
                            showStatus={false}
                            showIntegrationLogs={true}
                            showGridActionVersions={false}
                            uniqueIdentifier={"id"}
                        />
                    </TableViewHook>

                </CardContent>

            </Card>

        </div>
    );
}

IntegrationLogsListHook.propTypes = {
    tableStyle: PropTypes.object,
    title: PropTypes.string,
};

export default IntegrationLogsListHook;